import React, { useMemo } from 'react'
import {
  Box,
  Typography,
  Slider,
  Grid2 as Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme
} from '@mui/material'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'
import { PainsType } from 'types'
import {
  SentimentSatisfiedAlt,
  SentimentSatisfied,
  SentimentNeutral,
  SentimentDissatisfied,
  SentimentVeryDissatisfied
} from '@mui/icons-material'

interface PainFormProps {
  painLevel: number
  setPainLevel: (level: number) => void
  painLocation: PainsType
  setPainLocation: (location: PainsType) => void
  painDate: Dayjs | null
  setPainDate: (date: Dayjs | null) => void
  painTime: Dayjs | null
  setPainTime: (time: Dayjs | null) => void
}

const painLocations: { value: PainsType; label: string }[] = [
  { value: 'Head', label: 'Cabeça' },
  { value: 'Limbs', label: 'Braços e/ou pernas' },
  { value: 'Chest', label: 'Peito' },
  { value: 'Abdomen', label: 'Abdômen' },
  { value: 'Back', label: 'Costas' },
  { value: 'Neck', label: 'Pescoço' },
  { value: 'Generalized', label: 'Generalizada' },
  { value: 'Other', label: 'Outro' }
]

const painLevelDescriptions = [
  'Muito leve, quase imperceptível',
  'Leve, não interfere',
  'Leve, às vezes perceptível',
  'Moderada, ainda tolerável',
  'Interfere em algumas atividades',
  'Difícil de ignorar',
  'Limita atividades diárias',
  'Intensa, dificulta ações',
  'Muito intensa, impede atividades',
  'Insuportável'
]

// Componente movido para fora para evitar recriação a cada render
const EmojiContainer: React.FC<
  React.PropsWithChildren<{ color: string }>
> = ({ color, children }) => {
  return (
    <Box
      sx={{
        fontSize: '24px',
        color: color,
        opacity: 1,
        transition: 'color 0.3s ease'
      }}
    >
      {children}
    </Box>
  )
}

const PainForm: React.FC<PainFormProps> = ({
  painLevel,
  setPainLevel,
  painLocation,
  setPainLocation,
  painDate,
  setPainDate,
  painTime,
  setPainTime
}) => {
  const theme = useTheme()
  const painLevelIndex = useMemo(() => painLevel - 1, [painLevel])

  const painColor = useMemo(() => {
    return [
      theme.palette.painSliderLow.main,
      theme.palette.painSliderYellow.main,
      theme.palette.painSliderMid.main,
      theme.palette.painSliderOrange.main,
      theme.palette.painSliderHigh.main
    ]
  }, [painLevel])

  const getPainColor = (level: number) => {
    if (level <= 3) return theme.palette.painSliderLow.main
    if (level <= 7) return theme.palette.painSliderMid.main
    return theme.palette.painSliderHigh.main
  }

  const sliderGradient = `linear-gradient(to right, ${theme.palette.painSliderLow.main}, ${theme.palette.painSliderMid.main}, ${theme.palette.painSliderHigh.main})`
  return (
    <Box sx={{ p: 2 }}>
      {/* Pain Level Section */}
      <Box sx={{ mb: 4 }}>
        {/* Location Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant='h6' sx={{ mb: 2, fontWeight: 'bold' }}>
            Onde a dor está localizada?
          </Typography>

          <FormControl fullWidth>
            <InputLabel>Localização da dor</InputLabel>
            <Select
              value={painLocation}
              label='Localização da dor'
              onChange={e => setPainLocation(e.target.value as PainsType)}
            >
              {painLocations.map(location => (
                <MenuItem key={location.value} value={location.value}>
                  {location.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Typography variant='h6' sx={{ mb: 2, fontWeight: 'bold' }}>
          Qual o nível de dor?
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
          Considere 1 uma dor leve e 10 a pior dor que o paciente pode sentir
        </Typography>

        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant='body1' sx={{ mb: 2 }}>
            {painLevelDescriptions[painLevelIndex]}
          </Typography>
          <Typography
            variant='h2'
            sx={{
              fontWeight: 'bold',
              color: getPainColor(painLevel),
              mb: 2
            }}
          >
            {painLevel}
          </Typography>
        </Box>

        <Box sx={{ px: 2, mb: 2 }}>
          <Slider
            value={painLevel}
            onChange={(_, newValue) => setPainLevel(newValue as number)}
            min={1}
            max={10}
            step={1}
            sx={{
              color: getPainColor(painLevel),
              '& .MuiSlider-thumb': {
                width: 24,
                height: 24,
                backgroundColor: theme.palette.background.paper,
                border: `2px solid ${getPainColor(painLevel)}`,
                boxShadow: `0 2px 8px rgba(0,0,0,0.25)`,
                '&:hover': {
                  boxShadow: `0 4px 12px rgba(0,0,0,0.35)`
                }
              },
              '& .MuiSlider-track': {
                background: sliderGradient,
                border: 'none',
                height: 8,
                borderRadius: 4
              },
              '& .MuiSlider-rail': {
                background: sliderGradient,
                opacity: 0.3,
                height: 8,
                borderRadius: 4
              },
              '& .MuiSlider-mark': {
                display: 'none'
              }
            }}
            aria-label='Nível de dor (1 a 10)'
            getAriaValueText={value => `Nível de dor ${value} de 1 a 10`}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
          <EmojiContainer color={painColor[0]}>
            <SentimentSatisfiedAlt />
          </EmojiContainer>
          <EmojiContainer color={painColor[1]}>
            <SentimentSatisfied />
          </EmojiContainer>
          <EmojiContainer color={painColor[2]}>
            <SentimentNeutral />
          </EmojiContainer>
          <EmojiContainer color={painColor[3]}>
            <SentimentDissatisfied />
          </EmojiContainer>
          <EmojiContainer color={painColor[4]}>
            <SentimentVeryDissatisfied />
          </EmojiContainer>
        </Box>
      </Box>

      {/* Date and Time Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant='h6' sx={{ mb: 2, fontWeight: 'bold' }}>
          Quando a dor começou?
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <DatePicker
                label='Data'
                value={painDate}
                onChange={newValue => setPainDate(newValue)}
                format='DD/MM/YYYY'
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TimePicker
                label='Hora'
                value={painTime}
                onChange={newValue => setPainTime(newValue)}
                format='HH:mm'
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>
    </Box>
  )
}

export default PainForm
